import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  getAccessibleTeams,
  getAppSettings,
  getPublicTeamLiveStatuses,
  getPublicTeams,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { deriveLiveStatusPillState } from "@/features/live/components/LiveStatusPill";
import {
  publicAppSettingsKey,
  publicHostKey,
  useViewerHostKey,
} from "@/lib/queryClient";
import type { AccessibleTeam, PublicTeam, TeamAccessSource } from "@/lib/types";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { useAuthStore } from "@/stores/auth";
import { TeamCard } from "../components/TeamCard";

type DirectoryGroups = {
  memberships: AccessibleTeam[];
  operational: AccessibleTeam[];
  discovery: PublicTeam[];
};

const ACCESS_PRIORITY: Record<TeamAccessSource, number> = {
  membership: 0,
  club_admin: 1,
  super_admin: 2,
};

function compareTeamsByNameThenId(a: PublicTeam, b: PublicTeam): number {
  return (
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) ||
    a.id.localeCompare(b.id)
  );
}

/** Stable, defensive partition for the three directory lanes.
 *
 * The API already emits one row per team using membership > club admin >
 * super admin precedence. Keeping that rule here prevents a malformed or
 * temporarily mixed cache from duplicating a team across lanes. Each lane is
 * then sorted by locale-aware, case-insensitive name and id so its order is
 * deterministic even if the network response arrives in a different order.
 */
export function groupTeamDirectory(
  accessibleTeams: AccessibleTeam[],
  publicTeams: PublicTeam[],
): DirectoryGroups {
  const accessibleById = new Map<string, AccessibleTeam>();
  for (const team of accessibleTeams) {
    const current = accessibleById.get(team.id);
    if (
      !current ||
      ACCESS_PRIORITY[team.access_source] < ACCESS_PRIORITY[current.access_source]
    ) {
      accessibleById.set(team.id, team);
    }
  }

  const memberships: AccessibleTeam[] = [];
  const operational: AccessibleTeam[] = [];
  for (const team of accessibleById.values()) {
    if (team.access_source === "membership") memberships.push(team);
    else operational.push(team);
  }

  const discovery: PublicTeam[] = [];
  const publicSeen = new Set<string>();
  for (const team of publicTeams) {
    if (accessibleById.has(team.id) || publicSeen.has(team.id)) continue;
    publicSeen.add(team.id);
    discovery.push(team);
  }

  memberships.sort(compareTeamsByNameThenId);
  operational.sort(compareTeamsByNameThenId);
  discovery.sort(compareTeamsByNameThenId);

  return { memberships, operational, discovery };
}

/** `/teams` — the club directory for members and public visitors.
 *
 * Public discovery remains anonymous and host-partitioned. Once `/api/me`
 * finishes hydrating an authenticated identity, a separate private query adds
 * every team the server says that caller can reach on this club host. The
 * private key includes host + club + user and is deliberately not one of the
 * public cache domains preserved at logout.
 */
export function TeamsIndexPage() {
  const authMe = useAuthStore((state) => state.me);
  const authHydrating = useAuthStore((state) => state.hydrating);
  const clubId = authMe?.current_club?.id ?? "";
  const signedIn = authMe !== null;
  const hasClubContext = Boolean(clubId);
  const accessibleTeamsKey = useViewerHostKey("accessible-teams");

  const teamsQuery = useQuery({
    queryKey: publicHostKey("public-teams"),
    queryFn: getPublicTeams,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const accessibleTeamsQuery = useQuery({
    queryKey: accessibleTeamsKey,
    queryFn: getAccessibleTeams,
    enabled: !authHydrating && signedIn && hasClubContext,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });

  const settingsLiveEnabled = settingsQuery.data
    ? coerceBool(settingsQuery.data.settings.live_enabled)
    : true;
  const teamLiveStatusesQuery = useQuery({
    queryKey: publicHostKey("public-team-statuses"),
    queryFn: getPublicTeamLiveStatuses,
    refetchInterval: settingsLiveEnabled ? 10_000 : false,
    refetchIntervalInBackground: false,
    enabled: settingsLiveEnabled,
  });
  const teamLiveBySlug = useMemo(() => {
    const map = new Map<string, { active: boolean; ready: boolean }>();
    for (const entry of teamLiveStatusesQuery.data?.teams ?? []) {
      map.set(entry.slug, { active: entry.active, ready: entry.ready });
    }
    return map;
  }, [teamLiveStatusesQuery.data]);
  const teamLiveReachable = teamLiveStatusesQuery.data?.reachable;

  const groups = useMemo(
    () => groupTeamDirectory(accessibleTeamsQuery.data ?? [], teamsQuery.data ?? []),
    [accessibleTeamsQuery.data, teamsQuery.data],
  );

  const clubTitle = (settingsQuery.data?.settings?.club_title ?? "").trim();
  const clubIntro = (settingsQuery.data?.settings?.season_intro ?? "").trim();
  const clubLogoUrl = settingsQuery.data?.assets?.club_logo_url ?? "";
  const headingTitle = clubTitle || "teams";
  const headingIntro =
    clubIntro ||
    "Browse our club teams. Pick one to see matches, replays, and live broadcasts.";

  useDocumentMeta({
    title: clubTitle ? `${clubTitle} · tktic` : "Teams · tktic",
    description:
      clubIntro ||
      (clubTitle
        ? `Browse public team pages for ${clubTitle}.`
        : "Browse public team pages on tktic."),
    ogType: "website",
  });

  const liveStateFor = (team: PublicTeam) =>
    deriveLiveStatusPillState(
      teamLiveReachable,
      teamLiveBySlug.get(team.slug),
    );

  const showOperationalSection =
    groups.operational.length > 0 ||
    Boolean(authMe?.is_club_admin || authMe?.is_super_admin);

  return (
    <main
      data-testid="teams-index"
      className="min-h-[60vh] bg-canvas text-ink"
    >
      <header className="border-b border-rule px-4 py-8 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-3">
          <p className="eyebrow">tktic · teams</p>
          <div className="flex items-center gap-4">
            {clubLogoUrl ? (
              <img
                src={clubLogoUrl}
                alt=""
                data-testid="teams-index-club-logo"
                className="h-12 w-12 shrink-0 rounded-inset object-contain sm:h-16 sm:w-16"
              />
            ) : null}
            <h1
              data-testid="teams-index-heading"
              className="font-display text-3xl tracking-display text-ink sm:text-4xl"
            >
              {headingTitle}
            </h1>
          </div>
          <p
            data-testid="teams-index-intro"
            className="max-w-2xl font-body text-base text-ink-secondary"
          >
            {headingIntro}
          </p>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 py-10 sm:px-6">
        {signedIn && hasClubContext ? (
          accessibleTeamsQuery.isLoading ? (
            <DirectorySection
              id="your-teams"
              eyebrow="Your access"
              title="Your teams"
              description="Teams where you hold a direct membership."
              testId="teams-index-memberships"
            >
              <DirectoryState
                kind="loading"
                testId="teams-access-loading"
                message="Loading your team access…"
              />
            </DirectorySection>
          ) : accessibleTeamsQuery.isError ? (
            <DirectorySection
              id="your-teams"
              eyebrow="Your access"
              title="Your teams"
              description="Teams where you hold a direct membership."
              testId="teams-index-memberships"
            >
              <DirectoryState
                kind="error"
                testId="teams-access-error"
                message="Couldn’t load your team access. Public teams are still available below."
              />
            </DirectorySection>
          ) : (
            <>
              <DirectorySection
                id="your-teams"
                eyebrow="Your access"
                title="Your teams"
                description="Teams where you hold a direct membership."
                count={groups.memberships.length}
                testId="teams-index-memberships"
              >
                {groups.memberships.length > 0 ? (
                  <TeamGrid testId="teams-index-membership-grid">
                    {groups.memberships.map((team) => (
                      <li key={team.id} className="h-full">
                        <TeamCard
                          team={team}
                          visibility={team.public ? "public" : "private"}
                          liveState={team.public ? liveStateFor(team) : undefined}
                        />
                      </li>
                    ))}
                  </TeamGrid>
                ) : (
                  <DirectoryState
                    kind="empty"
                    testId="teams-index-membership-empty"
                    title="No team memberships yet"
                    message="Teams you join in this club will appear here. You can still browse public teams below."
                  />
                )}
              </DirectorySection>

              {showOperationalSection ? (
                <DirectorySection
                  id="operational-access"
                  eyebrow="Club operations"
                  title="Operational access"
                  description="Team pages available for playback troubleshooting through your club or platform administration role. Coaching feedback stays restricted to team members with coaching access."
                  count={groups.operational.length}
                  testId="teams-index-operational"
                >
                  {groups.operational.length > 0 ? (
                    <TeamGrid testId="teams-index-operational-grid">
                      {groups.operational.map((team) => (
                        <li key={team.id} className="h-full">
                          <TeamCard
                            team={team}
                            visibility={team.public ? "public" : "private"}
                            liveState={team.public ? liveStateFor(team) : undefined}
                          />
                        </li>
                      ))}
                    </TeamGrid>
                  ) : (
                    <DirectoryState
                      kind="empty"
                      testId="teams-index-operational-empty"
                      title="No operational teams yet"
                      message="Teams available for playback troubleshooting through your administration role will appear here."
                    />
                  )}
                </DirectorySection>
              ) : null}
            </>
          )
        ) : null}

        <DirectorySection
          id="explore-public-teams"
          eyebrow="Club directory"
          title="Explore public teams"
          description="Public team pages you can browse without additional access."
          count={!teamsQuery.isLoading && !teamsQuery.isError ? groups.discovery.length : undefined}
          testId="teams-index-discovery"
        >
          {teamsQuery.isLoading ? (
            <DirectoryState
              kind="loading"
              testId="teams-index-loading"
              message="Loading teams…"
            />
          ) : teamsQuery.isError ? (
            <DirectoryState
              kind="error"
              testId="teams-index-error"
              message="Couldn’t load the public team list. Please refresh in a moment."
            />
          ) : (teamsQuery.data ?? []).length === 0 ? (
            <PublicEmptyState signedIn={signedIn} />
          ) : groups.discovery.length === 0 ? (
            <DirectoryState
              kind="empty"
              testId="teams-index-discovery-empty"
              title="You’re already connected"
              message="Every public team is already listed in your access above."
            />
          ) : (
            <TeamGrid testId="teams-index-grid">
              {groups.discovery.map((team) => (
                <li key={team.id} className="h-full">
                  <TeamCard
                    team={team}
                    visibility="public"
                    liveState={liveStateFor(team)}
                  />
                </li>
              ))}
            </TeamGrid>
          )}
        </DirectorySection>
      </div>
    </main>
  );
}

function DirectorySection({
  id,
  eyebrow,
  title,
  description,
  count,
  testId,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  count?: number;
  testId: string;
  children: ReactNode;
}) {
  const headingId = `${id}-heading`;
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <header className="mb-5 border-b border-rule pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="eyebrow">{eyebrow}</p>
            <h2 id={headingId} className="mt-1 font-display text-2xl text-ink">
              {title}
            </h2>
          </div>
          {count !== undefined ? (
            <span
              aria-label={`${count} ${count === 1 ? "team" : "teams"}`}
              className="w-fit rounded-pill border border-rule bg-elevated px-2.5 py-1 font-mono text-xs text-ink-muted"
            >
              {count}
            </span>
          ) : null}
        </div>
        <span aria-hidden="true" className="accent-rule mt-3 w-10" />
        <p className="mt-3 max-w-2xl font-body text-sm text-ink-secondary">
          {description}
        </p>
      </header>
      {children}
    </section>
  );
}

function TeamGrid({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <ul
      data-testid={testId}
      role="list"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      {children}
    </ul>
  );
}

function DirectoryState({
  kind,
  testId,
  title,
  message,
}: {
  kind: "loading" | "error" | "empty";
  testId: string;
  title?: string;
  message: string;
}) {
  return (
    <div
      data-testid={testId}
      role={kind === "error" ? "alert" : kind === "loading" ? "status" : undefined}
      aria-live={kind === "loading" ? "polite" : undefined}
      className="rounded-panel border border-rule bg-surface p-5"
    >
      {title ? <h3 className="font-display text-base text-ink">{title}</h3> : null}
      <p
        className={`${title ? "mt-1 " : ""}font-body text-sm ${
          kind === "error" ? "text-destructive" : "text-ink-secondary"
        }`}
      >
        {message}
      </p>
    </div>
  );
}

function PublicEmptyState({ signedIn }: { signedIn: boolean }) {
  return (
    <div
      data-testid="teams-index-empty"
      className="flex flex-col items-start gap-3 rounded-panel border border-rule bg-surface p-6"
    >
      <h3 className="font-display text-lg tracking-tight text-ink">
        No public team pages yet
      </h3>
      <p className="font-body text-sm text-ink-secondary">
        {signedIn
          ? "This club hasn’t published any public team pages yet."
          : "Sign in if you’re part of a team — team admins can opt in to a public page from Coach Settings."}
      </p>
      {!signedIn ? (
        <Link
          to="/login"
          data-testid="teams-index-empty-login"
          className="font-display text-sm font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:decoration-[3px]"
        >
          Sign in
        </Link>
      ) : null}
    </div>
  );
}
